import { RouterProvider } from "@tanstack/react-router";
import { ConvexProviderWithAuth, ConvexReactClient } from "convex/react";
import { ConvexQueryCacheProvider } from "convex-helpers/react/cache";
import type { ReactNode } from "react";
import { StrictMode, useCallback, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { router } from "./router";
import { ImpersonationProvider } from "./impersonation";
import { Provider } from "./components/ui/provider";

type AuthStatus = {
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchAccessToken: () => Promise<string>;
};

const convexUrl = process.env.PUBLIC_CONVEX_URL as string;
const tenantId = process.env.PUBLIC_AZURE_TENANT_ID as string;
const clientId = process.env.PUBLIC_AZURE_AUTH_CLIENT_ID as string;
const scope = `${process.env.PUBLIC_AZURE_AUTH_AUDIENCE as string}/access_as_user`;
const authority = `https://login.microsoftonline.com/${tenantId}`;
const redirectUri = process.env.PUBLIC_CEMMBASE_URL as string;

const convex = new ConvexReactClient(convexUrl);

const msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority,
    redirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
});

void msalInstance.initialize().then(async () => {
  const redirectResponse = await msalInstance.handleRedirectPromise();
  if (redirectResponse?.account) {
    msalInstance.setActiveAccount(redirectResponse.account);
  }

  if (!msalInstance.getActiveAccount()) {
    const account = msalInstance.getAllAccounts()[0];
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

msalInstance.addEventCallback(event => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS
    || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
  ) {
    const account = event.payload && "account" in event.payload ? event.payload.account : null;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

function getMsalErrorCode(error: unknown): string | null {
  if (error && typeof error === "object" && "errorCode" in error && typeof error.errorCode === "string") {
    return error.errorCode;
  }

  return null;
}

function isRecoverableMsalSessionError(error: unknown): boolean {
  const errorCode = getMsalErrorCode(error);
  if (
    errorCode === "interaction_required"
    || errorCode === "login_required"
    || errorCode === "consent_required"
    || errorCode === "no_tokens_found"
    || errorCode === "monitor_window_timeout"
    || errorCode === "timed_out"
  ) {
    return true;
  }

  return error instanceof Error && /\b(timed_out|interaction_required|login_required|consent_required)\b/i.test(error.message);
}

function waitForRedirect(): Promise<string> {
  return new Promise(() => undefined);
}

function useConvexMicrosoftEntraAuth(): AuthStatus {
  const { instance, inProgress, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const fetchAccessToken = useCallback(async () => {
    const currentAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (!currentAccount) {
      throw new Error("Not authenticated");
    }

    try {
      const response = await instance.acquireTokenSilent({
        account: currentAccount,
        scopes: [scope],
      });

      return response.accessToken;
    } catch (error) {
      if (!isRecoverableMsalSessionError(error) || inProgress !== "none") {
        throw error;
      }

      await instance.acquireTokenRedirect({
        account: currentAccount,
        scopes: [scope],
      });

      return waitForRedirect();
    }
  }, [inProgress, instance]);

  return useMemo(
    () => ({
      isLoading: inProgress !== "none" || account === null,
      isAuthenticated: account !== null,
      fetchAccessToken,
    }),
    [account, fetchAccessToken, inProgress],
  );
}

function AppProviders({ children }: { children: ReactNode }) {
  return (
    <Provider>
      <MsalProvider instance={msalInstance}>
        <ConvexProviderWithAuth client={convex} useAuth={useConvexMicrosoftEntraAuth}>
          <ConvexQueryCacheProvider>
            <ImpersonationProvider>
              {children}
            </ImpersonationProvider>
          </ConvexQueryCacheProvider>
        </ConvexProviderWithAuth>
      </MsalProvider>
    </Provider>
  );
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>
);

(import.meta.hot.data.root ??= createRoot(elem)).render(app);
